import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Clock, Loader2, Calendar, Ticket, Users, DollarSign, MapPin, Copy, CreditCard } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConfirmTransactionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { language } = useLanguage();
  
  const [status, setStatus] = useState('loading');
  const [transactionData, setTransactionData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const hasConfirmed = useRef(false);

  // Translations
  const tr = {
    en: {
      loading: 'Confirming...',
      pleaseWait: 'Please wait',
      missingToken: 'Missing confirmation token',
      serverError: 'Server communication error',
      confirmError: 'Error during confirmation',
      
      successTitle: 'Transfer request confirmed!',
      successMessage: 'Your confirmation has been successfully recorded.',
      event: 'Event',
      date: 'Date',
      waitingOther: 'Waiting for the other party',
      buyerNotConfirmed: 'The buyer has not yet confirmed the transfer.',
      sellerNotConfirmed: 'The seller has not yet confirmed the transfer.',
      accessAccount: 'Access my account',
      
      bothConfirmedTitle: 'Ticket transfer fully confirmed! 🎉',
      bothConfirmedMessage: 'Both parties have confirmed this transfer. It can now proceed.',
      status: 'Status',
      buyerConfirmed: '✓ Buyer confirmed',
      sellerConfirmed: '✓ Seller confirmed',
      
      alreadyConfirmedTitle: 'Already confirmed',
      alreadyConfirmedMessage: 'You have already confirmed this transfer.',
      buyer: 'Buyer:',
      seller: 'Seller:',
      confirmed: '✓ Confirmed',
      pending: 'Pending',
      
      expiredTitle: 'Link expired',
      expiredMessage: 'This confirmation link has expired or is invalid.',
      contactSupport: 'If you have any questions, please contact support.',
      
      errorTitle: 'Confirmation error',
      backToHome: 'Back to home',
      
      // New translations for ticket details
      ticketDetails: 'Ticket details',
      numberOfTickets: 'Number of tickets',
      section: 'Section',
      row: 'Row',
      seats: 'Seats',
      platform: 'Platform',
      referenceNumber: 'Reference number',
      copyReference: 'Copy',
      referenceCopied: 'Reference number copied!',
      paymentSummary: 'Payment summary',
      ticketPrice: 'Ticket price',
      platformFee: 'Platform fee',
      youPay: 'You pay',
      youReceive: 'You receive',
      useReferenceNote: 'Use this reference number in the payment comment for tracking.'
    },
    fr: {
      loading: 'Confirmation en cours...',
      pleaseWait: 'Veuillez patienter',
      missingToken: 'Token de confirmation manquant',
      serverError: 'Erreur de communication avec le serveur',
      confirmError: 'Erreur lors de la confirmation',
      
      successTitle: 'Demande de transfert confirmée!',
      successMessage: 'Votre confirmation a été enregistrée avec succès.',
      event: 'Événement',
      date: 'Date',
      waitingOther: 'En attente de l\'autre partie',
      buyerNotConfirmed: 'L\'acheteur n\'a pas encore confirmé le transfert.',
      sellerNotConfirmed: 'Le vendeur n\'a pas encore confirmé le transfert.',
      accessAccount: 'Accéder à mon compte',
      
      bothConfirmedTitle: 'Transfert de billets entièrement confirmé! 🎉',
      bothConfirmedMessage: 'Les deux parties ont confirmé ce transfert. Il peut maintenant procéder.',
      status: 'Statut',
      buyerConfirmed: '✓ Acheteur confirmé',
      sellerConfirmed: '✓ Vendeur confirmé',
      
      alreadyConfirmedTitle: 'Déjà confirmé',
      alreadyConfirmedMessage: 'Vous avez déjà confirmé ce transfert.',
      buyer: 'Acheteur:',
      seller: 'Vendeur:',
      confirmed: '✓ Confirmé',
      pending: 'En attente',
      
      expiredTitle: 'Lien expiré',
      expiredMessage: 'Ce lien de confirmation a expiré ou est invalide.',
      contactSupport: 'Si vous avez des questions, veuillez contacter le support.',
      
      errorTitle: 'Erreur de confirmation',
      backToHome: 'Retour à l\'accueil',
      
      // New translations for ticket details
      ticketDetails: 'Détails des billets',
      numberOfTickets: 'Nombre de billets',
      section: 'Section',
      row: 'Rangée',
      seats: 'Sièges',
      platform: 'Plateforme',
      referenceNumber: 'Numéro de référence',
      copyReference: 'Copier',
      referenceCopied: 'Numéro de référence copié!',
      paymentSummary: 'Résumé du paiement',
      ticketPrice: 'Prix des billets',
      platformFee: 'Frais de plateforme',
      youPay: 'Vous payez',
      youReceive: 'Vous recevez',
      useReferenceNote: 'Utilisez ce numéro de référence dans le commentaire lors du paiement pour le suivi.'
    }
  };

  const t = tr[language] || tr.fr;

  useEffect(() => {
    if (token && !hasConfirmed.current) {
      hasConfirmed.current = true;
      confirmTransaction();
    } else if (!token) {
      setStatus('error');
      setErrorMessage(t.missingToken);
    }
  }, [token]);

  const confirmTransaction = async () => {
    try {
      const res = await fetch(`${API}/transactions/confirm-by-token?token=${token}`);
      const responseText = await res.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(t.serverError);
      }
      
      if (!res.ok) {
        throw new Error(data.detail || t.confirmError);
      }
      
      setTransactionData(data);
      
      if (data.already_confirmed) {
        setStatus('already_confirmed');
      } else if (data.both_confirmed) {
        setStatus('both_confirmed');
      } else {
        setStatus('success');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center py-12">
            <Loader2 className="h-16 w-16 text-brand-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">{t.loading}</h2>
            <p className="text-gray-500 mt-2">{t.pleaseWait}</p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.successTitle}</h2>
            <p className="text-gray-600 mb-6">{t.successMessage}</p>
            
            {transactionData && (
              <>
                {/* Reference Number - Copyable */}
                <div className="bg-gradient-to-r from-brand-50 to-blue-50 border border-brand-200 rounded-lg p-4 max-w-md mx-auto mb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-left">
                      <p className="text-xs text-gray-500">{t.referenceNumber}</p>
                      <p className="text-lg font-bold text-brand-700 font-mono">
                        {transactionData.reference_number || transactionData.id}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-brand-300 text-brand-700 hover:bg-brand-100"
                      onClick={() => {
                        navigator.clipboard.writeText(transactionData.reference_number || transactionData.id);
                        toast.success(t.referenceCopied);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      {t.copyReference}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-left">{t.useReferenceNote}</p>
                </div>

                {/* Event & Ticket Details */}
                <Card className="bg-gray-50 border-0 text-left max-w-md mx-auto mb-4">
                  <CardContent className="pt-6">
                    <h4 className="font-semibold text-gray-900 mb-3 border-b pb-2">{t.ticketDetails}</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Ticket className="h-5 w-5 text-brand-600" />
                        <div>
                          <p className="text-sm text-gray-500">{t.event}</p>
                          <p className="font-semibold">{transactionData.event_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-brand-600" />
                        <div>
                          <p className="text-sm text-gray-500">{t.date}</p>
                          <p className="font-semibold">{transactionData.event_date} {transactionData.event_time && `à ${transactionData.event_time}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-brand-600" />
                        <div>
                          <p className="text-sm text-gray-500">{t.numberOfTickets}</p>
                          <p className="font-semibold">{transactionData.ticket_count} billet{transactionData.ticket_count > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      {transactionData.section && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-brand-600" />
                          <div>
                            <p className="text-sm text-gray-500">{t.section}</p>
                            <p className="font-semibold">{transactionData.section}</p>
                          </div>
                        </div>
                      )}
                      {transactionData.row && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-brand-600" />
                          <div>
                            <p className="text-sm text-gray-500">{t.row}</p>
                            <p className="font-semibold">{transactionData.row}</p>
                          </div>
                        </div>
                      )}
                      {transactionData.seats && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-brand-600" />
                          <div>
                            <p className="text-sm text-gray-500">{t.seats}</p>
                            <p className="font-semibold">{transactionData.seats}</p>
                          </div>
                        </div>
                      )}
                      {transactionData.ticket_platform && (
                        <div className="flex items-center gap-3">
                          <CreditCard className="h-5 w-5 text-brand-600" />
                          <div>
                            <p className="text-sm text-gray-500">{t.platform}</p>
                            <p className="font-semibold">{transactionData.ticket_platform === 'Autre' ? transactionData.custom_platform : transactionData.ticket_platform}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Summary */}
                <Card className="bg-blue-50 border-blue-200 text-left max-w-md mx-auto mb-6">
                  <CardContent className="pt-6">
                    <h4 className="font-semibold text-gray-900 mb-3 border-b border-blue-200 pb-2">{t.paymentSummary}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t.ticketPrice}</span>
                        <span className="font-medium">{transactionData.price?.toFixed(2) || '0.00'} $</span>
                      </div>
                      {transactionData.calculated_fees && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{t.platformFee}</span>
                            <span className="font-medium">{transactionData.calculated_fees.platform_fee?.toFixed(2) || '0.00'} $</span>
                          </div>
                          <div className="border-t border-blue-200 pt-2 mt-2">
                            <div className="flex justify-between font-bold text-green-700">
                              <span>{t.youPay}</span>
                              <span>{transactionData.calculated_fees.buyer_pays?.toFixed(2) || transactionData.total_amount?.toFixed(2) || '0.00'} $</span>
                            </div>
                          </div>
                        </>
                      )}
                      {!transactionData.calculated_fees && transactionData.total_amount && (
                        <div className="border-t border-blue-200 pt-2 mt-2">
                          <div className="flex justify-between font-bold text-green-700">
                            <span>{t.youPay}</span>
                            <span>{transactionData.total_amount?.toFixed(2)} $</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto mb-6">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-left">
                  <p className="font-medium text-amber-800">{t.waitingOther}</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {transactionData?.buyer_confirmed && !transactionData?.seller_confirmed 
                      ? t.sellerNotConfirmed
                      : t.buyerNotConfirmed}
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/auth')} 
              className="bg-brand-600 hover:bg-brand-700"
            >
              {t.accessAccount}
            </Button>
          </div>
        );
      
      case 'both_confirmed':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.bothConfirmedTitle}</h2>
            <p className="text-gray-600 mb-6">{t.bothConfirmedMessage}</p>
            
            {transactionData && (
              <>
                {/* Reference Number - Copyable */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto mb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-left">
                      <p className="text-xs text-gray-500">{t.referenceNumber}</p>
                      <p className="text-lg font-bold text-green-700 font-mono">
                        {transactionData.reference_number || transactionData.id}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green-300 text-green-700 hover:bg-green-100"
                      onClick={() => {
                        navigator.clipboard.writeText(transactionData.reference_number || transactionData.id);
                        toast.success(t.referenceCopied);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      {t.copyReference}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-left">{t.useReferenceNote}</p>
                </div>

                {/* Event & Ticket Details */}
                <Card className="bg-green-50 border-green-200 text-left max-w-md mx-auto mb-4">
                  <CardContent className="pt-6">
                    <h4 className="font-semibold text-green-900 mb-3 border-b border-green-200 pb-2">{t.ticketDetails}</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Ticket className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm text-green-700">{t.event}</p>
                          <p className="font-semibold text-green-900">{transactionData.event_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm text-green-700">{t.date}</p>
                          <p className="font-semibold text-green-900">{transactionData.event_date} {transactionData.event_time && `à ${transactionData.event_time}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm text-green-700">{t.numberOfTickets}</p>
                          <p className="font-semibold text-green-900">{transactionData.ticket_count} billet{transactionData.ticket_count > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      {transactionData.section && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm text-green-700">{t.section}</p>
                            <p className="font-semibold text-green-900">{transactionData.section}</p>
                          </div>
                        </div>
                      )}
                      {transactionData.row && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm text-green-700">{t.row}</p>
                            <p className="font-semibold text-green-900">{transactionData.row}</p>
                          </div>
                        </div>
                      )}
                      {transactionData.seats && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm text-green-700">{t.seats}</p>
                            <p className="font-semibold text-green-900">{transactionData.seats}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3 pt-3 border-t border-green-200">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm text-green-700">{t.status}</p>
                          <p className="font-semibold text-green-900">{t.buyerConfirmed} · {t.sellerConfirmed}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Summary */}
                <Card className="bg-blue-50 border-blue-200 text-left max-w-md mx-auto mb-6">
                  <CardContent className="pt-6">
                    <h4 className="font-semibold text-gray-900 mb-3 border-b border-blue-200 pb-2">{t.paymentSummary}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t.ticketPrice}</span>
                        <span className="font-medium">{transactionData.price?.toFixed(2) || '0.00'} $</span>
                      </div>
                      {transactionData.calculated_fees && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{t.platformFee}</span>
                            <span className="font-medium">{transactionData.calculated_fees.platform_fee?.toFixed(2) || '0.00'} $</span>
                          </div>
                          <div className="border-t border-blue-200 pt-2 mt-2">
                            <div className="flex justify-between font-bold text-green-700">
                              <span>{t.youPay}</span>
                              <span>{transactionData.calculated_fees.buyer_pays?.toFixed(2) || transactionData.total_amount?.toFixed(2) || '0.00'} $</span>
                            </div>
                          </div>
                        </>
                      )}
                      {!transactionData.calculated_fees && transactionData.total_amount && (
                        <div className="border-t border-blue-200 pt-2 mt-2">
                          <div className="flex justify-between font-bold text-green-700">
                            <span>{t.youPay}</span>
                            <span>{transactionData.total_amount?.toFixed(2)} $</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
            
            <Button 
              onClick={() => navigate('/auth')} 
              className="bg-brand-600 hover:bg-brand-700"
            >
              {t.accessAccount}
            </Button>
          </div>
        );
      
      case 'already_confirmed':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.alreadyConfirmedTitle}</h2>
            <p className="text-gray-600 mb-6">{t.alreadyConfirmedMessage}</p>
            
            {transactionData && (
              <Card className="bg-gray-50 border-0 text-left max-w-md mx-auto mb-6">
                <CardContent className="pt-6">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.buyer}</span>
                      <span className={transactionData.buyer_confirmed ? "text-green-600 font-medium" : "text-amber-600"}>
                        {transactionData.buyer_confirmed ? t.confirmed : t.pending}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.seller}</span>
                      <span className={transactionData.seller_confirmed ? "text-green-600 font-medium" : "text-amber-600"}>
                        {transactionData.seller_confirmed ? t.confirmed : t.pending}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Button 
              onClick={() => navigate('/auth')} 
              className="bg-brand-600 hover:bg-brand-700"
            >
              {t.accessAccount}
            </Button>
          </div>
        );
      
      case 'error':
      default:
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.errorTitle}</h2>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <p className="text-sm text-gray-500 mb-6">{t.contactSupport}</p>
            <Button 
              onClick={() => navigate('/')} 
              variant="outline"
            >
              {t.backToHome}
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-50/30 to-cyan-50/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-xl">
        <CardContent className="p-6">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmTransactionPage;
